import type { ColDef, ColGroupDef, GridOptions } from '@ag-grid-community/core';
import { DeviceStateAnalysisAsset, Vendors } from '@cdw-ae/common-data';
import type {
  FilterBarField,
  SelectedFilterField,
} from '@cdw-ae/filter-bar-interfaces';
import { forkJoin, map, Observable, take } from 'rxjs';
import { TailwindColors } from '../../../../../common/constants/tailwind-colors';
import { DevicesDataService } from '../../../../../common/services/device/devices-data.service';
import { OuPathService } from '../../../../../common/services/ou-path/ou-path.service';
import {
  filterToSupportedStateAnalysisFields,
  StateAnalysisReportFilterSettings,
} from '../../../../../common/services/route-config/configs/filter-bar-config.config';
import { createChromeVersionAgeReportConfig } from '../configs/chrome-version-age-report.config';
import type {
  GaugeSummaryConfig,
  ReportData,
} from '../report-viewer/report-viewer.model';
import { BaseReportHandler } from './base-report.handler';

/**
 * Handler for Chrome Version Age by OU aggregate reports
 * Manages filtering, grid configuration, and data processing for Chrome Version Age reports
 * Structure: Chrome Version (Level 1) -> OU Path (Levels 2+)
 */
export class ChromeVersionAgeReportHandler extends BaseReportHandler {
  /**
   * Static method to load Chrome Version Age report data from API
   * Fetches device state analysis data, processes it with filters, and returns configured report data
   * @param devicesDataService Service to fetch device data
   * @param filters Optional filters to apply to the data
   * @returns Observable of ReportData ready for display
   */
  static load(
    devicesDataService: DevicesDataService,
    filters?: SelectedFilterField[]
  ): Observable<ReportData> {
    // Get filters to apply (use provided filters or initial filters)
    const allFilters =
      filters || ChromeVersionAgeReportHandler.getInitialFilters() || [];

    // Filter to only include fields supported by the state-analysis endpoint
    const filtersToApply = filterToSupportedStateAnalysisFields(allFilters);

    // Fetch both device state analysis and latest OS versions in parallel
    return forkJoin({
      deviceStateAnalysis:
        devicesDataService.getDeviceStateAnalysis(filtersToApply),
      latestOsVersions: devicesDataService.getLatestOsVersions(),
    }).pipe(
      take(1),
      map(({ deviceStateAnalysis, latestOsVersions }) => {
        const latestChromeVersion = latestOsVersions?.[Vendors.GOOGLE] ?? 0;

        // Aggregate data (no filtering needed - API already filtered)
        const processedData = ChromeVersionAgeReportHandler.processRawData(
          deviceStateAnalysis.data,
          []
        );

        // Create report config with processed data
        const reportConfig = createChromeVersionAgeReportConfig(processedData);

        // Store metadata generically in the config for handler initialization
        if (reportConfig.aggregateConfig) {
          Object.assign(reportConfig.aggregateConfig, {
            metadata: latestChromeVersion,
          });
        }

        return reportConfig;
      })
    );
  }

  private chromeVersionRanks = new Map<number, number>(); // Maps version to rank (1 = latest, 2 = second latest, etc.)
  private latestChromeVersion = 0;
  private totalDevicesCount = 0;
  private versionSummary = {
    atLatest: 0,
    upTo2VersionsBehind: 0,
    moreThan2VersionsBehind: 0,
    unknown: 0,
  };
  private versionSummaryPercentages = {
    atLatest: 0,
    upTo2VersionsBehind: 0,
    moreThan2VersionsBehind: 0,
    unknown: 0,
  };

  // Gauge colors configuration — Figma donut palette (saturated fills, not pastel row backgrounds)
  private readonly gaugeColors = {
    green: [{ name: 'Green', value: TailwindColors.chartGreen }],
    orange: [{ name: 'Orange', value: TailwindColors.chartDarkOrange }],
    red: [{ name: 'Red', value: TailwindColors.chartRed }],
    gray: [{ name: 'Gray', value: TailwindColors.gray200 }],
  };

  /**
   * Calculate version ranks using the actual latest version from the API
   * This should be called before grid initialization
   * Versions are incremental (144, 143, 142, 141...) so we can calculate rank by distance from latest
   * @param data The report data
   * @param latestVersion The latest Chrome OS version from API
   */
  private calculateVersionRanks(data: unknown[], latestVersion: number): void {
    // Extract unique Chrome versions from the data
    const versions = Array.from(
      new Set(
        data.map((item) =>
          Number((item as Record<string, unknown>).chromeVersion)
        )
      )
    ).filter((v) => !Number.isNaN(v));

    // Only calculate ranks if we have the actual latest version from API
    if (latestVersion && latestVersion > 0) {
      // Calculate rank based on distance from latest version
      // Latest = rank 1, Latest-1 = rank 2, Latest-2 = rank 3, etc.
      versions.forEach((version) => {
        const rank = latestVersion - version + 1;
        this.chromeVersionRanks.set(version, rank);
      });
    }
  }

  /**
   * Get background color based on Chrome version rank
   */
  private getVersionColor(version: number): string {
    const rank = this.chromeVersionRanks.get(version);
    if (!rank) return 'transparent';

    // Row backgrounds use pastel tokens
    if (rank === 1) return TailwindColors.pastelLime; // latest
    if (rank <= 3) return TailwindColors.pastelPeach; // up to 2 behind
    return TailwindColors.pastelRose; // 3+ behind
  }
  getColumnDefs(): (ColDef | ColGroupDef)[] {
    const config = this.reportData.aggregateConfig;
    if (!config) return [];

    // Return: Active Devices + Release Date (skip chromeVersion and ouPath - they'll be tree columns)
    const columns = config.columns.slice(2);

    // Add aggregation functions to columns
    return columns.map((col) => {
      if ('field' in col && col.field === 'activeDevices') {
        return {
          ...col,
          sortable: true, // Enable 3-way sorting: Asc → Desc → No Sort (EDUS-2153)
          aggFunc: 'sum', // Sum up active devices for parent nodes
        };
      }
      if ('field' in col && col.field === 'releaseDate') {
        return {
          ...col,
          sortable: true, // Enable 3-way sorting: Asc → Desc → No Sort (EDUS-2153)
          // Custom aggregation function that returns the first non-null value only for level 0
          aggFunc: (params) => {
            // Only show release date for Chrome version level (level 0)
            if (params.rowNode.level === 0) {
              // Get all leaf nodes under this Chrome version group
              const leafNodes: unknown[] = [];
              params.rowNode.allLeafChildren?.forEach((child) => {
                const releaseDate = child.data?.releaseDate;
                if (releaseDate != null && releaseDate !== '') {
                  leafNodes.push(releaseDate);
                }
              });

              // Return the first release date found
              return leafNodes.length > 0 ? leafNodes[0] : null;
            }
            // For OU levels, return null (will show as empty)
            return null;
          },
        };
      }
      return col;
    });
  }

  /**
   * Calculate version summary statistics from the data
   * Filters to leaf nodes and groups by Chrome version
   * @param data The report data
   * @param latestVersion The latest Chrome OS version from API
   */
  private calculateVersionSummary(
    data: unknown[],
    latestVersion: number
  ): void {
    if (!data || data.length === 0) {
      return;
    }

    // Cast data to expected structure
    const chromeData = data as Array<{
      chromeVersion: string;
      activeDevices: number;
      ouPath?: string;
    }>;

    // Group rows by Chrome version and sum active devices.
    // Each row in chromeData represents a distinct (chromeVersion, exact-OU)
    // cell produced by processRawData's lookup-key reduce — no roll-up
    // parents exist, so iterating every row is safe. The previous leaf-filter
    // (parent OUs whose path is a prefix of another row's path) was vestigial
    // from a pre-reduce data shape and silently dropped legitimate parent-OU
    // rows from the total (EDUS-2438 surfaced this 1-device shortfall once
    // the unknown-bucket recovery exposed it).
    const versionTotals = new Map<number, number>();

    chromeData.forEach((item) => {
      const version = Number(item.chromeVersion);
      const devices = item.activeDevices || 0;

      if (!Number.isNaN(version)) {
        const currentTotal = versionTotals.get(version) || 0;
        versionTotals.set(version, currentTotal + devices);
      }
    });

    this.latestChromeVersion = latestVersion;

    // Reset summary
    this.versionSummary = {
      atLatest: 0,
      upTo2VersionsBehind: 0,
      moreThan2VersionsBehind: 0,
      unknown: 0,
    };
    this.totalDevicesCount = 0;

    // Classify each version's total
    versionTotals.forEach((devices, version) => {
      this.totalDevicesCount += devices;

      if (version === latestVersion) {
        this.versionSummary.atLatest += devices;
      } else if (version >= latestVersion - 2 && version < latestVersion) {
        this.versionSummary.upTo2VersionsBehind += devices;
      } else if (version < latestVersion - 2) {
        this.versionSummary.moreThan2VersionsBehind += devices;
      }
    });

    // Handle unknown versions (NaN)
    chromeData.forEach((item) => {
      const version = Number(item.chromeVersion);
      if (Number.isNaN(version)) {
        const devices = item.activeDevices || 0;
        this.versionSummary.unknown += devices;
        this.totalDevicesCount += devices;
      }
    });

    // Calculate percentages for gauges (0-100)
    if (this.totalDevicesCount > 0) {
      this.versionSummaryPercentages = {
        atLatest: Math.round(
          (this.versionSummary.atLatest / this.totalDevicesCount) * 100
        ),
        upTo2VersionsBehind: Math.round(
          (this.versionSummary.upTo2VersionsBehind / this.totalDevicesCount) *
            100
        ),
        moreThan2VersionsBehind: Math.round(
          (this.versionSummary.moreThan2VersionsBehind /
            this.totalDevicesCount) *
            100
        ),
        unknown: Math.round(
          (this.versionSummary.unknown / this.totalDevicesCount) * 100
        ),
      };
    } else {
      this.versionSummaryPercentages = {
        atLatest: 0,
        upTo2VersionsBehind: 0,
        moreThan2VersionsBehind: 0,
        unknown: 0,
      };
    }
  }

  /**
   * Initialize the handler with data to calculate version ranks and summary
   * Extracts the latest Chrome version from the report config metadata
   * @param data The report data
   */
  public override initializeWithData(data: unknown[]): void {
    // Extract latest version from report config metadata
    const latestVersion = this.reportData?.aggregateConfig?.metadata as
      | number
      | undefined;

    // Exit early if no valid API version - cannot calculate ranks or summary
    if (!latestVersion || latestVersion === 0) {
      return;
    }

    this.latestChromeVersion = latestVersion;

    this.calculateVersionRanks(data, latestVersion);
    this.calculateVersionSummary(data, latestVersion);
  }

  /**
   * Get gauge summary configuration for the Chrome Version Age report
   * @param filteredData Optional filtered data to calculate summary from
   */
  public override getGaugeSummaryConfig(
    filteredData?: unknown[]
  ): GaugeSummaryConfig {
    // If filtered data is provided, recalculate the summary using the same latest version
    if (filteredData && filteredData.length > 0) {
      this.calculateVersionSummary(filteredData, this.latestChromeVersion);
    }

    return {
      headerText: `${this.totalDevicesCount} Devices`,
      cards: [
        {
          title: `At the Latest (${this.latestChromeVersion})`,
          value: this.versionSummary.atLatest,
          percentage: this.versionSummaryPercentages.atLatest,
          borderColor: TailwindColors.chartGreen,
          gaugeColor: this.gaugeColors.green,
        },
        {
          title: 'Up to 2 Versions Behind',
          value: this.versionSummary.upTo2VersionsBehind,
          percentage: this.versionSummaryPercentages.upTo2VersionsBehind,
          borderColor: TailwindColors.chartDarkOrange,
          gaugeColor: this.gaugeColors.orange,
        },
        {
          title: 'More than 2 Versions Behind',
          value: this.versionSummary.moreThan2VersionsBehind,
          percentage: this.versionSummaryPercentages.moreThan2VersionsBehind,
          borderColor: TailwindColors.chartRed,
          gaugeColor: this.gaugeColors.red,
        },
        {
          title: 'Unknown',
          value: this.versionSummary.unknown,
          percentage: this.versionSummaryPercentages.unknown,
          borderColor: TailwindColors.gray200,
          gaugeColor: this.gaugeColors.gray,
        },
      ],
    };
  }

  getGridOptions(): GridOptions {
    const config = this.reportData.aggregateConfig;
    if (!config) return {};

    return {
      treeData: true,
      animateRows: true,
      groupDefaultExpanded: 0, // Start collapsed
      getDataPath: (data: Record<string, unknown>) => {
        // Three-level hierarchy: Chrome Version -> OU Path segments
        // Example: chromeVersion="131", ouPath="/Engineering/Development"
        // Returns: ["131", "Engineering", "/Development"]
        const chromeVersion = String(data.chromeVersion || '');
        const ouPath = String(data.ouPath || '');

        // Parse OU path into segments (first segment no slash, subsequent ones with slash)
        const formattedSegments = OuPathService.parsePathForGrid(ouPath);

        // Combine Chrome version + OU segments
        return [chromeVersion, ...formattedSegments];
      },
      autoGroupColumnDef: {
        headerName: 'Major Chrome Version / OU Path',
        flex: 2,
        sortable: false, // Disable sorting on tree hierarchy column (EDUS-2153)
        cellRendererParams: {
          suppressCount: true,
        },
        cellStyle: (params) => {
          // Only color top-level Chrome version cells (level 0)
          if (params.node.level === 0 && params.node.key) {
            const version = Number(params.node.key);
            if (!Number.isNaN(version)) {
              return {
                backgroundColor: this.getVersionColor(version),
              };
            }
            // 'Unknown' bucket — apply pastel gray row background (Reports background palette)
            return {
              backgroundColor: TailwindColors.gray100,
            };
          }
          return undefined;
        },
      },
      getChildCount: (data: Record<string, unknown>) => {
        // Show the total active devices for aggregated parent nodes
        return data[config.aggregationField] as number;
      },
      defaultColDef: {
        resizable: true,
        // sortable removed - columns are non-sortable by default
        // Individual columns can enable sorting as needed (EDUS-2153)
      },
    };
  }

  static override getFilterBarSettings(): FilterBarField[] {
    return StateAnalysisReportFilterSettings;
  }

  static override getInitialFilters(): SelectedFilterField[] | null {
    return BaseReportHandler.getDefaultActiveStatusFilter(
      StateAnalysisReportFilterSettings
    );
  }

  /**
   * Process raw DeviceStateAnalysisAsset data into chrome version age format
   * Aggregates data by chrome version and OU
   * @param rawData The raw device state analysis data
   * @param _filters Filters to apply (unused - API already filters)
   * @param versionMap Optional version to release date lookup map
   */
  static override processRawData(
    rawData: unknown[],
    _filters: SelectedFilterField[]
  ): unknown[] {
    const deviceData = rawData as DeviceStateAnalysisAsset[];

    // Aggregate chrome version data
    const chromeVersionDataLookup = deviceData.reduce(
      (accumulator, data) => {
        // Route devices with no recent OS-version telemetry (osVersion null,
        // undefined, 0, or negative) into a synthetic 'Unknown' bucket so
        // they still contribute to the per-OU totals (EDUS-2438), matching
        // the EDUS-2304 fix for Storage by OU.
        const isUnknownVersion = !data.osVersion || data.osVersion <= 0;
        const chromeVersion: string | number = isUnknownVersion
          ? 'Unknown'
          : data.osVersion;
        const releaseDate = isUnknownVersion
          ? null
          : data.osVersionReleaseDate
            ? new Date(data.osVersionReleaseDate).toISOString()
            : null;
        const ouPath = data.groupingValue || 'Unknown OU';
        const status = data.status || 'Unknown';
        const lookupKey = `${chromeVersion}--${ouPath}`;
        if (!accumulator?.[lookupKey]) {
          accumulator[lookupKey] = {
            status,
            activeDevices: data.deviceCount,
            chromeVersion,
            ouPath,
            releaseDate,
          };
        } else {
          accumulator[lookupKey].activeDevices =
            Number(accumulator[lookupKey].activeDevices) + data.deviceCount;
        }

        return accumulator;
      },
      {} as Record<string, Record<string, string | number | null>>
    );

    return Object.values(chromeVersionDataLookup);
  }
}
